import { LOCAL_STORAGE_KEY } from '@/constants';
import { storage } from '@/utils/storageOperateApi';
// 注意：本模块被路由配置（routers/aiChat.js、routers/thirdparty.js）在 eager 包内静态引入，
// 若在此处静态 import '@/store'，会形成 route → openChatModeRestore → @/store → @/router → route 的循环依赖。
// 因此 store 仅在任务模式分支按需动态 import（@/store 已随主包加载，命中缓存、无额外网络请求）。

const SKIP_OPEN_CHAT_MODE_RESTORE_KEY = `${LOCAL_STORAGE_KEY.OPEN_CHAT_MODE}_SKIP_RESTORE`;

/**
 * 标记当前 tab 的下一次 AI 进宝首页挂载不自动恢复上次模式。
 */
export function markSkipOpenChatModeRestore() {
  window.sessionStorage.setItem(SKIP_OPEN_CHAT_MODE_RESTORE_KEY, '1');
}

/**
 * 消费当前 tab 的跳过恢复标记，避免新建会话被跨 tab 缓存带到其他模式。
 */
export function consumeSkipOpenChatModeRestore() {
  const shouldSkip = window.sessionStorage.getItem(SKIP_OPEN_CHAT_MODE_RESTORE_KEY) === '1';
  if (shouldSkip) {
    window.sessionStorage.removeItem(SKIP_OPEN_CHAT_MODE_RESTORE_KEY);
  }
  return shouldSkip;
}

/**
 * 只读窥探当前 tab 是否标记了"跳过下一次模式恢复"，不清除标记。
 * 供路由守卫判断是否恢复模式使用；标记的实际清除统一交给 chatHome 挂载时的 consume，
 * 以兼容全局搜索内嵌（不走路由、守卫不触发）等不经过路由守卫的场景。
 * @returns {boolean} 是否需要跳过本次模式恢复
 */
export function peekSkipOpenChatModeRestore() {
  return window.sessionStorage.getItem(SKIP_OPEN_CHAT_MODE_RESTORE_KEY) === '1';
}

/**
 * 在路由解析阶段计算"上次停留模式"的恢复重定向目标。
 * 放在路由守卫（而非 chatHome 组件 onMounted）执行，可避免先挂载聊天首页、
 * 待研究/任务模式懒加载 chunk 加载完成后才跳转所导致的空白闪烁与误输入丢失。
 * 注：URL 带 query.skipModeCache=1 或命中"跳过恢复"标记时，均不读取/恢复缓存的模式。
 * @param {import('vue-router').RouteLocationNormalized} to 目标路由
 * @returns {Promise<import('vue-router').RouteLocationRaw | undefined>} 需要重定向时解析为目标路由，否则解析为 undefined（放行停留聊天首页）
 */
export async function resolveOpenChatModeRestoreRedirect(to) {
  // thirdparty 上下文（comeinAichatIndex / /thirdparty 路径）：研究模式暂无 thirdparty 版本，不恢复研究模式，避免跳出 iframe
  const isThirdParty = to?.name === 'comeinAichatIndex' || String(to?.path || '').includes('/thirdparty/');

  // URL 显式带 skipModeCache=1：本次导航需要落在目标页本身，不读取/恢复缓存的模式（供三方跳转指定精确落地页时使用）
  if (String(to?.query?.skipModeCache) === '1') return undefined;

  // 当前 tab 标记跳过恢复：避免新建会话被缓存带到其他模式。
  // 此处只窥探不清除，标记由 chatHome 挂载时统一 consume（兼容不走路由的内嵌场景）
  if (peekSkipOpenChatModeRestore()) return undefined;

  const openChatMode = storage.get(LOCAL_STORAGE_KEY.OPEN_CHAT_MODE);
  if (openChatMode === 'task') {
    // 有缓存：上次停留在任务模式，清空选中会话后恢复到任务模式。
    // 动态 import 断开与 @/store 的静态循环依赖；@/store 已随主包加载，await 仅消耗一个微任务
    const { default: store } = await import('@/store');
    store.commit('openClaw/setUserSelectedSessionId', null);
    return { name: isThirdParty ? 'thirdpartyOpenClawChat' : 'openClawChat', replace: true };
  } else if (openChatMode === 'research' && !isThirdParty) {
    // 有缓存：上次停留在研究模式，恢复到研究模式。
    // 入口来源不在此固定写入：菜单点击由 navMenu 写 AI进宝菜单、Tab 切换写 顶部模式Tab，
    // 直接访问 URL 等无来源场景由目标页 consume 兜底为「直接访问链接」
    return { name: 'aiChatResearch', replace: true };
  } else if (!openChatMode && !isThirdParty) {
    // 无缓存（首次进入 AI 进宝）：默认打开研究模式，来源判定同上
    return { name: 'aiChatResearch', replace: true };
  }
  // 其余情况（如缓存为 chat）：停留在聊天首页
  return undefined;
}

/**
 * AI 进宝首页（aiChatIndex / comeinAichatIndex）路由 beforeEnter 守卫：
 * 在导航解析阶段直接重定向到上次停留的研究/任务模式。
 * @param {import('vue-router').RouteLocationNormalized} to 目标路由
 * @param {import('vue-router').RouteLocationNormalized} from 来源路由
 * @param {import('vue-router').NavigationGuardNext} next 放行/重定向回调
 * @returns {Promise<void>}
 */
export async function beforeEnterOpenChatHome(to, from, next) {
  const redirect = await resolveOpenChatModeRestoreRedirect(to);
  // 命中恢复目标则重定向，否则放行停留聊天首页
  if (redirect) {
    next(redirect);
    return;
  }
  next();
}
